
import React from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Profile } from '@/types/profiles';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';
import { PixelCharacter } from '@/components/PixelCharacter/PixelCharacter';
import { POSE_MAP, POSE_LABELS } from '@/components/PixelCharacter/poses';
import '@/components/PixelCharacter/pixel-animations.css';

interface EditEmployeeFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  position?: string;
  hourly_rate?: number;
  hire_date?: string;
  avatar_description?: string;
  avatar_pose?: string;
}

interface EditEmployeeDialogProps {
  employee: Profile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEmployeeUpdated: () => void;
}

const EditEmployeeDialog = ({ employee, open, onOpenChange, onEmployeeUpdated }: EditEmployeeDialogProps) => {
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();
  
  const form = useForm<EditEmployeeFormData>({
    defaultValues: {
      first_name: employee?.first_name || '',
      last_name: employee?.last_name || '',
      email: employee?.email || '',
      phone: employee?.phone || '',
      position: employee?.position || '',
      hourly_rate: employee?.hourly_rate || undefined,
      hire_date: employee?.hire_date || '',
      avatar_description: employee?.avatar_description || '',
      avatar_pose: employee?.avatar_pose || 'hammer',
    },
  });

  React.useEffect(() => {
    if (employee) {
      form.reset({
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        position: employee.position || '',
        hourly_rate: employee.hourly_rate || undefined,
        hire_date: employee.hire_date || '',
        avatar_description: employee.avatar_description || '',
        avatar_pose: employee.avatar_pose || 'hammer',
      });
    }
  }, [employee, form]);

  const onSubmit = async (data: EditEmployeeFormData) => {
    if (!employee) return;

    try {
      setLoading(true);

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_profile', {
          p_pin: pin,
          p_profile_id: employee.id,
          p_first_name: data.first_name,
          p_last_name: data.last_name,
          p_email: data.email,
          p_phone: data.phone || null,
          p_position: data.position || null,
          p_hourly_rate: data.hourly_rate || null,
          p_hire_date: data.hire_date || null,
          p_avatar_description: data.avatar_description || null,
          p_avatar_pose: data.avatar_pose || null,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            phone: data.phone || null,
            position: data.position || null,
            hourly_rate: data.hourly_rate || null,
            hire_date: data.hire_date || null,
            avatar_description: data.avatar_description || null,
            avatar_pose: data.avatar_pose || null,
          })
          .eq('id', employee.id);
        if (error) throw error;
      }

      toast({
        title: "Succès",
        description: "Employé mis à jour avec succès",
      });

      onOpenChange(false);
      onEmployeeUpdated();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Impossible de mettre à jour l'employé",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier l'employé</DialogTitle>
          <DialogDescription>
            Modifiez les informations de l'employé. Les changements seront sauvegardés immédiatement.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                rules={{ required: "Le prénom est requis" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prénom *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                rules={{ required: "Le nom est requis" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              rules={{ 
                required: "L'email est requis",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email invalide"
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Poste</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hourly_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taux horaire ($/h)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hire_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date d'embauche</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Section Personnage pixel art */}
            <div className="border-t pt-4 mt-2">
              <h3 className="text-sm font-semibold mb-3">Personnage pixel art</h3>

              <div className="flex gap-4 items-start">
                {/* Preview */}
                <div className="bg-slate-900 rounded-lg p-3 flex-shrink-0">
                  <PixelCharacter pose={form.watch('avatar_pose') || 'hammer'} size={80} />
                </div>

                <div className="flex-1 space-y-3">
                  <FormField
                    control={form.control}
                    name="avatar_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description du personnage</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: Dort sur le job, Boit son café..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="avatar_pose"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pose</FormLabel>
                        <FormControl>
                          <select
                            className="w-full border rounded px-2 py-2 text-sm"
                            value={field.value || 'hammer'}
                            onChange={field.onChange}
                          >
                            {Object.entries(POSE_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Mise à jour..." : "Mettre à jour"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditEmployeeDialog;
